/**
 * LINE Configuration Loader
 * Centralized service for loading LINE config from Database → LocalStorage
 * Single source of truth for all LINE-related services
 */

import type { LineNotificationConfig } from '../types/line'
import { logServiceError } from '../utils/errorLogger'

const STORAGE_KEY = 'line_channel_access_token'
const CONFIG_LOADED_KEY = 'line_config_loaded_at'

export class LineConfigLoader {
  private static instance: LineConfigLoader
  private config: LineNotificationConfig | null = null
  private loading: Promise<LineNotificationConfig> | null = null

  private constructor() {}

  static getInstance(): LineConfigLoader {
    if (!LineConfigLoader.instance) {
      LineConfigLoader.instance = new LineConfigLoader()
    }
    return LineConfigLoader.instance
  }

  /**
   * Load LINE config from Database and cache in localStorage
   * Returns cached config if recently loaded (within 5 minutes)
   */
  async loadConfig(forceRefresh = false): Promise<LineNotificationConfig> {
    // Return cached config if available and not forced refresh
    if (!forceRefresh && this.config) {
      return this.config
    }

    // Check if already loading
    if (this.loading) {
      return this.loading
    }

    // Check localStorage cache (valid for 5 minutes)
    if (!forceRefresh) {
      const cachedConfig = this.getCachedConfig()
      if (cachedConfig) {
        this.config = cachedConfig
        return cachedConfig
      }
    }

    // Load from database
    this.loading = this.loadFromDatabase()

    try {
      this.config = await this.loading
      return this.config
    } finally {
      this.loading = null
    }
  }

  /**
   * Get cached config from localStorage if valid
   */
  private getCachedConfig(): LineNotificationConfig | null {
    try {
      const token = localStorage.getItem(STORAGE_KEY)
      const loadedAt = localStorage.getItem(CONFIG_LOADED_KEY)

      if (!token || !loadedAt) {
        return null
      }

      // Check if cache is still valid (30 minutes)
      const loadedTime = parseInt(loadedAt, 10)
      const now = Date.now()
      const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes

      if (now - loadedTime > CACHE_DURATION) {
        console.log('📦 LINE config cache expired, will reload from DB')
        return null
      }

      console.log('✅ Using cached LINE config from localStorage')
      return {
        channelAccessToken: token,
        enabled: true
      }
    } catch (error) {
      console.warn('Failed to get cached config:', error)
      return null
    }
  }

  /**
   * Load config from database
   */
  private async loadFromDatabase(): Promise<LineNotificationConfig> {
    try {
      console.log('📡 Loading LINE config from database...')

      // Dynamic import to avoid circular dependencies
      const { settingsService } = await import('./SettingsService')

      const settings = await settingsService.getLineNotificationSettings()

      // Validate token
      if (!settings.channelAccessToken) {
        console.warn('⚠️  LINE Channel Access Token not found in database')
        console.warn('⚠️  Please configure LINE token in Settings page')
      }

      // Save to localStorage for quick access
      if (settings.channelAccessToken) {
        localStorage.setItem(STORAGE_KEY, settings.channelAccessToken)
        localStorage.setItem(CONFIG_LOADED_KEY, Date.now().toString())
        console.log('✅ LINE config loaded and cached successfully')
      }

      return {
        channelAccessToken: settings.channelAccessToken || '',
        enabled: settings.enabled
      }

    } catch (error) {
      console.error('❌ Failed to load LINE config from database:', error)

      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'LineConfigLoader',
        'loadFromDatabase'
      )

      // Return empty config - NO FALLBACK TO ENV
      console.warn('⚠️  LINE token must be configured in database (Settings page)')
      return {
        channelAccessToken: '',
        enabled: false
      }
    }
  }

  /**
   * Update config and sync to localStorage
   * Call this after updating settings in the database
   */
  async updateConfig(token: string, enabled: boolean): Promise<void> {
    try {
      console.log('🔄 Updating LINE config...', { enabled, hasToken: !!token })

      // Update cache
      this.config = {
        channelAccessToken: token,
        enabled
      }

      // Update localStorage
      if (token) {
        localStorage.setItem(STORAGE_KEY, token)
        localStorage.setItem(CONFIG_LOADED_KEY, Date.now().toString())
      } else {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(CONFIG_LOADED_KEY)
      }

      console.log('✅ LINE config updated successfully')
    } catch (error) {
      console.error('❌ Failed to update LINE config:', error)
      throw error
    }
  }

  /**
   * Get current token from cache (synchronous)
   * Returns null if not loaded yet
   */
  getTokenSync(): string | null {
    if (this.config) {
      return this.config.channelAccessToken
    }

    // Try localStorage as backup
    return localStorage.getItem(STORAGE_KEY)
  }

  /**
   * Clear cached config
   * Use this when user logs out or when token becomes invalid
   */
  clearCache(): void {
    console.log('🗑️  Clearing LINE config cache')
    this.config = null
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(CONFIG_LOADED_KEY)
  }

  /**
   * Check if config is loaded and valid
   */
  isConfigValid(): boolean {
    const token = this.getTokenSync()
    return !!token && token.length > 0
  }
}

// Export singleton instance
export const lineConfigLoader = LineConfigLoader.getInstance()
